import { message } from "antd";
import { useCallback, useRef } from "react";

import { updateAppointment } from "../../api/healthcare/Appointment/AppointmentApi";
import { updatePatient } from "../../api/healthcare/PatientApi";
import { Appointment } from "../../types/appointments/AppointmentTypes";
import { Patient } from "../../types/healthcare/PatientTypes";

interface UseEndConsultOptions {
	appointment: Appointment;
	patient: Patient | null;
	roomId: string;
}

interface UseEndConsultResult {
	endConsult: () => Promise<boolean>;
}

export function useEndConsult({ appointment, patient, roomId }: UseEndConsultOptions): UseEndConsultResult {
	// Single owner of the finalisation writes — a boolean is not enough: both PUTs are awaited, so two synchronous callers would both pass it.
	const endConsultRef = useRef<Promise<boolean> | null>(null);

	const endConsult = useCallback((): Promise<boolean> => {
		if (endConsultRef.current) return endConsultRef.current;

		const attempt = (async (): Promise<boolean> => {
			try {
				await updateAppointment({ ...appointment, status: "Completed" });
			} catch {
				message.error("Failed to complete consultation");
				return false;
			}

			// The consult is committed by the line above — a failed patient PUT is degraded, not failed.
			try {
				if (patient) await updatePatient(patient.id, patient);
				message.success("Consultation completed successfully");
			} catch {
				// Event name + room id ONLY — an AxiosError here carries the whole patient record in config.data.
				console.error("consult.patient.finalise_update_failed", { roomId });
				message.warning("Consult saved — patient details did not update");
			}
			return true;
		})();

		endConsultRef.current = attempt;
		// A completed consult must never be finalised twice; a failed one must stay retryable.
		attempt.then((completed) => {
			if (!completed && endConsultRef.current === attempt) endConsultRef.current = null;
		});
		return attempt;
	}, [appointment, patient, roomId]);

	return { endConsult };
}
